import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { ArrowUp, Paperclip, Square, X, FileText, Image as ImageIcon, Sparkles, Mic, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AgentAttachment } from '@/services/agent/service'
import { FinderDialog } from '@/modules/finder'
import type { FinderItem } from '@/modules/finder/types'
import MediaPickerModal from '@/components/media/MediaPickerModal'
import { useMediaPicker } from '@/hooks/useMediaPicker'
import type { MediaAsset } from '@/services/folders/mediaTypes'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import FoundationPickerDialog from './FoundationPickerDialog'
import HeftProfilePickerDialog from './HeftProfilePickerDialog'
import HeftStoryPickerDialog from './HeftStoryPickerDialog'
import { formatHeftProfileChipLabel } from '@/modules/campaigns/components/brief-strategist/brief-strategist-handoff'

type Props = {
    value: string
    onChange: (value: string) => void
    onSubmit: () => void
    onStop: () => void
    isStreaming: boolean
    attachments: AgentAttachment[]
    onAttachmentsChange: (next: AgentAttachment[]) => void
    suggestionsSlot?: React.ReactNode
    suggestionsCount?: number
    suggestionsPreview?: string
    disabled?: boolean
    placeholder?: string
    /** When true, the Sources attachment row starts expanded (Studio setup mode). */
    defaultSourcesExpanded?: boolean
}

export default function Composer({
    value,
    onChange,
    onSubmit,
    onStop,
    isStreaming,
    attachments,
    onAttachmentsChange,
    suggestionsSlot,
    suggestionsCount,
    suggestionsPreview,
    disabled,
    placeholder = 'Message agent...',
    defaultSourcesExpanded = false,
}: Props) {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const [docsPickerOpen, setDocsPickerOpen] = useState(false)
    const [foundationPickerOpen, setFoundationPickerOpen] = useState(false)
    const [heftProfilePickerOpen, setHeftProfilePickerOpen] = useState(false)
    const [heftStoryPickerOpen, setHeftStoryPickerOpen] = useState(false)
    const [sourcesExpanded, setSourcesExpanded] = useState(defaultSourcesExpanded)
    const [suggestionsExpanded, setSuggestionsExpanded] = useState(false)

    const mergeAttachments = (newOnes: AgentAttachment[]) => {
        const merged = [...attachments]
        const existing = new Set(merged.map(a => `${a.kind}:${a.id}`))
        for (const a of newOnes) {
            const key = `${a.kind}:${a.id}`
            if (a.kind === 'heft_profile') {
                // One selected Persona per subject is clearer than stacking multiple hats.
                for (let i = merged.length - 1; i >= 0; i--) {
                    if (merged[i]?.kind === 'heft_profile' && merged[i]?.id === a.id) {
                        merged.splice(i, 1)
                    }
                }
                existing.delete(key)
            }
            if (existing.has(key)) continue
            merged.push(a)
            existing.add(key)
        }
        onAttachmentsChange(merged)
    }

    const chipTitle = (a: AgentAttachment) => {
        if (a.kind === 'heft_profile') {
            const base = formatHeftProfileChipLabel(a)
            const lensLabel = typeof (a as any).lensLabel === 'string' ? (a as any).lensLabel : typeof (a as any).lens === 'string' ? (a as any).lens : ''
            const viewName = typeof (a as any).viewName === 'string' ? (a as any).viewName : ''
            const suffix = [lensLabel, viewName].filter(Boolean).join(' · ')
            return suffix ? `${base} — ${suffix}` : base
        }
        if (a.kind === 'heft_story') {
            const t = a.title || a.id
            const subject = (a as any).subjectName ? String((a as any).subjectName) : ''
            return subject ? `${t} — ${subject}` : t
        }
        return (a as any).name || a.id
    }

    const { pickerProps, openPicker } = useMediaPicker({
        multiSelect: false,
        onConfirm: (assets: MediaAsset[]) => {
            const next: AgentAttachment[] = assets.map(a => ({
                kind: 'document',
                id: a._id,
                name: a.name,
                dataType: a.data_type ?? null,
                url: a.url ?? null,
            }))
            mergeAttachments(next)
        },
    })

    useEffect(() => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = 'auto'
        const next = Math.min(el.scrollHeight, 200)
        el.style.height = `${next}px`
    }, [value])

    useEffect(() => {
        if (defaultSourcesExpanded && attachments.length > 0) {
            setSourcesExpanded(true)
        }
    }, [defaultSourcesExpanded, attachments.length])

    const handleSubmit = (e?: FormEvent) => {
        e?.preventDefault()
        if (!value.trim() || isStreaming) return
        onSubmit()
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSubmit()
        }
    }

    const canSubmit = value.trim().length > 0 && !isStreaming && !disabled

    return (
        <form onSubmit={handleSubmit} className='mx-auto max-w-3xl'>
            <div
                className={cn(
                    'group relative flex flex-col rounded-2xl bg-white shadow-sm transition-all',
                    'ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-primary-main/30 focus-within:shadow-md'
                )}
            >
                {suggestionsSlot && (suggestionsCount || 0) > 0 ? (
                    <button
                        type='button'
                        className='flex items-center gap-2 px-4 pt-3 text-left text-xs text-slate-600 hover:text-slate-900'
                        onClick={() => setSuggestionsExpanded(v => !v)}
                    >
                        {suggestionsExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
                        <span className='font-semibold'>Suggestions</span>
                        <span className='text-slate-400'>({suggestionsCount || 0})</span>
                        {!suggestionsExpanded ? (
                            <span className='min-w-0 truncate text-slate-400'>{String(suggestionsPreview || 'Tap to view')}</span>
                        ) : null}
                    </button>
                ) : null}

                {suggestionsSlot && suggestionsExpanded ? (
                    <div className='px-4 pb-2 pt-2'>
                        <div className='rounded-xl border border-slate-200 bg-slate-50 p-2'>
                            {suggestionsSlot}
                        </div>
                    </div>
                ) : null}

                {attachments.length > 0 ? (
                    <button
                        type='button'
                        className='flex items-center gap-2 px-4 pt-3 text-left text-xs text-slate-600 hover:text-slate-900'
                        onClick={() => setSourcesExpanded(v => !v)}
                    >
                        {sourcesExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
                        <span className='font-semibold'>Sources</span>
                        <span className='text-slate-400'>({attachments.length})</span>
                        {!sourcesExpanded ? (
                            <span className='min-w-0 truncate text-slate-400'>{chipTitle(attachments[0])}</span>
                        ) : null}
                    </button>
                ) : null}

                {attachments.length > 0 && sourcesExpanded ? (
                    <div className='px-4 pb-2 pt-2'>
                        <div className='flex min-w-0 flex-wrap gap-1.5'>
                            {attachments.map((a, idx) => (
                                <span
                                    key={`${a.kind}-${a.id}-${idx}`}
                                    className='inline-flex max-w-[260px] items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700'
                                >
                                    <span className='truncate'>{chipTitle(a)}</span>
                                    <button
                                        type='button'
                                        onClick={() => onAttachmentsChange(attachments.filter((_, i) => i !== idx))}
                                        className='text-slate-500 hover:text-slate-800'
                                        title='Remove'
                                    >
                                        <X className='h-3 w-3' />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}

                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    rows={1}
                    disabled={disabled}
                    className='block w-full resize-none rounded-2xl border-0 bg-transparent px-4 pt-3.5 pb-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0'
                />

                <div className='flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 px-3 py-2.5'>
                    <div className='flex min-w-0 items-center gap-2'>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type='button'
                                    disabled={disabled || isStreaming}
                                    className={cn(
                                        'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                                        disabled || isStreaming
                                            ? 'bg-slate-100 text-slate-300'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    )}
                                    title='Attach'
                                >
                                    <Paperclip className='h-4 w-4' />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='start' className='w-56'>
                                <DropdownMenuItem onClick={() => setDocsPickerOpen(true)}>
                                    <FileText className='mr-2 h-4 w-4' />
                                    Attach documents
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={openPicker}>
                                    <ImageIcon className='mr-2 h-4 w-4' />
                                    Attach images (Media Center)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setFoundationPickerOpen(true)}>
                                    <Sparkles className='mr-2 h-4 w-4' />
                                    Attach a Foundation
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setHeftProfilePickerOpen(true)}>
                                    <Mic className='mr-2 h-4 w-4' />
                                    Attach a HEFT profile
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setHeftStoryPickerOpen(true)}>
                                    <Mic className='mr-2 h-4 w-4' />
                                    Attach a HEFT story
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {attachments.length === 0 ? (
                            <span className='hidden sm:inline text-[11px] text-slate-400'>
                                <kbd className='rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] font-medium text-slate-500'>
                                    Enter
                                </kbd>
                                <span className='mx-1.5'>send</span>
                                <kbd className='rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] font-medium text-slate-500'>
                                    Shift+Enter
                                </kbd>
                                <span className='ml-1.5'>newline</span>
                            </span>
                        ) : null}
                    </div>

                    <span className='text-[11px] text-slate-400 sr-only'>
                        <kbd className='rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] font-medium text-slate-500'>
                            Enter
                        </kbd>
                        <span className='mx-1.5'>send</span>
                        <kbd className='rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] font-medium text-slate-500'>
                            Shift+Enter
                        </kbd>
                        <span className='ml-1.5'>newline</span>
                    </span>

                    {isStreaming ? (
                        <button
                            type='button'
                            onClick={onStop}
                            className='flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white transition-colors hover:bg-slate-800'
                            title='Stop generating'
                        >
                            <Square className='h-3.5 w-3.5 fill-white' />
                        </button>
                    ) : (
                        <button
                            type='submit'
                            disabled={!canSubmit}
                            className={cn(
                                'flex h-8 w-8 items-center justify-center rounded-lg transition-all',
                                canSubmit
                                    ? 'bg-primary-main text-white hover:bg-primary-main/90 shadow-sm'
                                    : 'bg-slate-100 text-slate-300'
                            )}
                            title='Send message'
                        >
                            <ArrowUp className='h-4 w-4' />
                        </button>
                    )}
                </div>
            </div>

            <FinderDialog
                open={docsPickerOpen}
                onOpenChange={setDocsPickerOpen}
                mode='select'
                showOnly='all'
                selectionMode='multiple'
                selectText='Attach'
                onSelect={(files: FinderItem[]) => {
                    const next: AgentAttachment[] = files
                        .filter(f => f.type !== 'folder')
                        .map(f => ({
                        kind: 'document',
                        id: f.id,
                        name: f.name,
                    }))
                    mergeAttachments(next)
                }}
            />

            <MediaPickerModal
                {...pickerProps}
                title='Attach images'
                allowUpload
                mode='folder'
            />

            <FoundationPickerDialog
                open={foundationPickerOpen}
                onOpenChange={setFoundationPickerOpen}
                onAdd={mergeAttachments}
            />
            <HeftProfilePickerDialog
                open={heftProfilePickerOpen}
                onOpenChange={setHeftProfilePickerOpen}
                onAdd={mergeAttachments}
            />
            <HeftStoryPickerDialog
                open={heftStoryPickerOpen}
                onOpenChange={setHeftStoryPickerOpen}
                onAdd={mergeAttachments}
            />
        </form>
    )
}
